import { clsx } from "clsx"
import type { CSSProperties, ReactNode } from "react"

import { type SX, theme, type ThemeKeys } from "../../theme"
import { ButtonGroup } from "../ButtonGroup"
import { Icon, type IconName } from "../Icon"
import { Text } from "../Text"
import styles from "./BlankSlate.module.css"

export interface BlankSlateProps {
  sx?: SX
  variant?: "vertical" | "horizontal"
  children?: ReactNode
}

export function BlankSlate(props: BlankSlateProps) {
  const { sx, variant = "vertical", ...otherProps } = props

  return (
    <article
      {...otherProps}
      style={sx}
      className={clsx(styles.root, styles[`variant-${variant}`])}
    />
  )
}

interface BlankSlateIconProps {
  name: IconName
  lineColor?: ThemeKeys<"color">
  backgroundColor?: ThemeKeys<"color">
  borderColor?: ThemeKeys<"color">
  borderStyle?: CSSProperties["borderStyle"]
  // TODO: Change size to be a number, to be done in PBD-1854
  size?: "small" | "medium" | 48 | "large" | "xlarge"
}

export function BlankSlateIcon(props: BlankSlateIconProps) {
  const {
    name,
    lineColor = "grey11",
    backgroundColor = "grey7",
    borderColor,
    borderStyle,
    size = "medium",
    ...otherProps
  } = props

  return (
    <div
      {...otherProps}
      className={clsx(styles.icon, styles[`icon-${size}`])}
      style={{
        backgroundColor: theme.color[backgroundColor],
        borderColor: borderColor ? theme.color[borderColor] : undefined,
        borderStyle,
        borderWidth: 1,
      }}
    >
      <Icon className={styles.iconChildren} color={lineColor} name={name} />
    </div>
  )
}

const titleSizeVariant = {
  regular: "normal",
  big: "h2",
} as const

export interface BlankSlateTitleProps {
  size?: keyof typeof titleSizeVariant
  children?: ReactNode
}

export function BlankSlateTitle(props: BlankSlateTitleProps) {
  const { size = "regular", ...restProps } = props

  return (
    <Text
      {...restProps}
      noSelect
      align="center"
      variant={titleSizeVariant[size]}
    />
  )
}
interface BlankSlateDescriptionProps {
  width?: number
  children?: ReactNode
}

export function BlankSlateDescription(props: BlankSlateDescriptionProps) {
  const { width } = props

  return (
    <Text
      {...props}
      noSelect
      align="center"
      variant="normal"
      color="grey11"
      sx={{ width }}
    />
  )
}

interface BlankSlateImageProps {
  fixedHeight?: boolean
  children?: ReactNode
}

export function BlankSlateImage(props: BlankSlateImageProps) {
  const { fixedHeight = true, ...otherProps } = props

  return (
    <div
      {...otherProps}
      className={clsx(styles.image, fixedHeight && styles.imageFixedHeight)}
    />
  )
}

export function BlankSlateActions(props: { children: ReactNode }) {
  return <ButtonGroup {...props} size="large" color="grey" />
}
